import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileSpreadsheet, FileText, FileDown, Printer } from "lucide-react";

export const PERIODS = ["daily", "weekly", "monthly", "quarterly", "yearly", "custom"];
const now = new Date();

export function usePeriod() {
  const [period, setPeriod] = useState("monthly");
  const [date, setDate] = useState(now.toISOString().slice(0, 10));
  const [from, setFrom] = useState(now.toISOString().slice(0, 10));
  const [to, setTo] = useState(now.toISOString().slice(0, 10));
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [quarter, setQuarter] = useState(Math.floor(now.getMonth() / 3) + 1);

  const buildParams = (extra = {}) => {
    const p = new URLSearchParams({ period });
    if (period === "daily" || period === "weekly") p.set("date", date);
    if (period === "custom") { p.set("date_from", from); p.set("date_to", to); }
    if (["monthly", "quarterly", "yearly"].includes(period)) p.set("year", year);
    if (period === "monthly") p.set("month", month);
    if (period === "quarterly") p.set("quarter", quarter);
    Object.entries(extra).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p;
  };

  const state = { period, setPeriod, date, setDate, from, setFrom, to, setTo, year, setYear, month, setMonth, quarter, setQuarter };
  return { ...state, buildParams };
}

export const PeriodBar = (p) => (
  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
    <div>
      <Label>Report Period</Label>
      <Select value={p.period} onValueChange={p.setPeriod}>
        <SelectTrigger data-testid="report-period"><SelectValue /></SelectTrigger>
        <SelectContent>{PERIODS.map((x) => <SelectItem key={x} value={x}>{x[0].toUpperCase() + x.slice(1)}</SelectItem>)}</SelectContent>
      </Select>
    </div>
    <div className="flex items-end gap-2 sm:col-span-2">
      {(p.period === "daily" || p.period === "weekly") && <div className="flex-1"><Label>{p.period === "weekly" ? "Any date in week" : "Date"}</Label><Input type="date" value={p.date} onChange={(e) => p.setDate(e.target.value)} data-testid="report-date" /></div>}
      {p.period === "custom" && <><div className="flex-1"><Label>From</Label><Input type="date" value={p.from} onChange={(e) => p.setFrom(e.target.value)} data-testid="report-from" /></div><div className="flex-1"><Label>To</Label><Input type="date" value={p.to} onChange={(e) => p.setTo(e.target.value)} data-testid="report-to" /></div></>}
      {["monthly", "quarterly", "yearly"].includes(p.period) && <div className="w-28"><Label>Year</Label><Input type="number" value={p.year} onChange={(e) => p.setYear(e.target.value)} data-testid="report-year" /></div>}
      {p.period === "monthly" && <div className="w-28"><Label>Month</Label><Input type="number" min={1} max={12} value={p.month} onChange={(e) => p.setMonth(e.target.value)} data-testid="report-month" /></div>}
      {p.period === "quarterly" && <div className="w-28"><Label>Quarter</Label><Select value={String(p.quarter)} onValueChange={(v) => p.setQuarter(Number(v))}><SelectTrigger data-testid="report-quarter"><SelectValue /></SelectTrigger><SelectContent>{[1, 2, 3, 4].map((q) => <SelectItem key={q} value={String(q)}>Q{q}</SelectItem>)}</SelectContent></Select></div>}
    </div>
  </div>
);

export function FranchisePicker({ fr, setFr }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => { if (q) api.get(`/franchise/search?q=${encodeURIComponent(q)}`).then(({ data }) => setResults(data)).catch(() => {}); else setResults([]); }, 200);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div>
      <Label>Franchise (optional)</Label>
      {fr ? (
        <div className="flex items-center justify-between rounded-lg border border-border bg-secondary/40 px-3 py-2 text-sm" data-testid="selected-fr">
          <span className="font-semibold">{fr.name} <span className="font-mono text-xs text-muted-foreground">{fr.referral_number}</span></span>
          <button className="text-xs text-rose-500" onClick={() => setFr(null)}>Clear</button>
        </div>
      ) : (
        <>
          <Input placeholder="All franchises — search to filter…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="fr-report-search" />
          {results.length > 0 && (
            <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-border bg-popover">
              {results.map((x) => (
                <button key={x.id} data-testid={`fr-opt-${x.referral_number}`} onClick={() => { setFr(x); setResults([]); setQ(""); }}
                  className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-accent">
                  <span>{x.name} <span className="font-mono text-xs text-muted-foreground">{x.referral_number}</span></span>
                  <span className="text-xs text-muted-foreground">{x.city}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export const ExportButtons = ({ onDownload }) => (
  <div className="flex flex-wrap gap-2">
    <Button size="sm" variant="outline" onClick={() => onDownload("excel")} data-testid="dl-excel"><FileSpreadsheet className="mr-1.5 h-4 w-4" />Excel</Button>
    <Button size="sm" variant="outline" onClick={() => onDownload("csv")} data-testid="dl-csv"><FileDown className="mr-1.5 h-4 w-4" />CSV</Button>
    <Button size="sm" variant="outline" onClick={() => onDownload("pdf")} data-testid="dl-pdf"><FileText className="mr-1.5 h-4 w-4" />PDF</Button>
    <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="print-report"><Printer className="mr-1.5 h-4 w-4" />Print</Button>
  </div>
);

export const StatCard = ({ l, v, accent }) => (
  <div className="rounded-xl border border-border bg-card p-4">
    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{l}</p>
    <p className={`mt-1.5 font-head text-2xl font-bold tabular ${accent === "emerald" ? "text-emerald-500" : accent === "amber" ? "text-amber-500" : accent === "violet" ? "text-violet-500" : accent === "rose" ? "text-rose-500" : accent === "primary" ? "text-primary" : ""}`}>{v}</p>
  </div>
);

export const ReportSection = ({ title, children }) => (
  <div><h3 className="mb-2 font-head text-sm font-semibold">{title}</h3><div className="overflow-hidden rounded-xl border border-border bg-card">{children}</div></div>
);

export const FilterCard = ({ children }) => (
  <div className="mb-5 rounded-xl border border-border bg-card p-5">{children}</div>
);
